import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import './EventFormulasPage.css';
import { authHeader } from '../services/authService';

const API_BASE = 'https://backend.ouest-pro-event.fr';

const getFullImageUrl = (relativeUrl) => {
    if (!relativeUrl) return '';
    if (relativeUrl.startsWith('http')) return relativeUrl;
    return `${API_BASE}${relativeUrl}`;
};

const createEmptyItem = () => ({ id: null, title: '', details: '' });

function EventFormulasPage() {
  const [formulas, setFormulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editingFormulaId, setEditingFormulaId] = useState(null);
  const [editingFormula, setEditingFormula] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [includeHidden, setIncludeHidden] = useState(false);
  const [formValues, setFormValues] = useState({
    name: '',
    description: '',
    is_visible: true,
    items: [createEmptyItem()],
  });

  const clearSelectedPhotos = () => {
    setPhotoPreviews((prev) => {
      prev.forEach((src) => URL.revokeObjectURL(src));
      return [];
    });
    setSelectedPhotos([]);
  };

  const fetchFormulas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = includeHidden ? '?include_hidden=true' : '';
      const res = await fetch(`${API_BASE}/admin/event-formulas${query}`, {
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
      });
      if (!res.ok) {
        throw new Error("Erreur lors du chargement des formules");
      }
      const data = await res.json();
      const fetchedFormulas = data.formulas || [];
      setFormulas(fetchedFormulas);
      if (editingFormulaId) {
        const updated = fetchedFormulas.find((f) => f.id === editingFormulaId);
        if (updated) {
          setEditingFormula(updated);
        }
      }
    } catch (err) {
      setError(err.message);
      setFormulas([]);
    } finally {
      setLoading(false);
    }
  }, [includeHidden, editingFormulaId]);

  useEffect(() => {
    fetchFormulas();
  }, [fetchFormulas]);

  useEffect(
    () => () => {
      photoPreviews.forEach((src) => URL.revokeObjectURL(src));
    },
    [photoPreviews]
  );

  const resetForm = () => {
    setEditingFormulaId(null);
    setEditingFormula(null);
    setFormValues({
      name: '',
      description: '',
      is_visible: true,
      items: [createEmptyItem()],
    });
    clearSelectedPhotos();
  };

  const handleEdit = (formula) => {
    setEditingFormulaId(formula.id);
    setEditingFormula(formula);
    clearSelectedPhotos();
    setFormValues({
      name: formula.name,
      description: formula.description || '',
      is_visible: !!formula.is_visible,
      items:
        formula.items && formula.items.length > 0
          ? formula.items.map((item) => ({
              id: item.id,
              title: item.title || '',
              details: item.details || '',
            }))
          : [createEmptyItem()],
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (formulaId) => {
    if (!window.confirm('Supprimer cette formule ?')) {
      return;
    }
    setIsSaving(true);
    try {
      const res = await fetch(`${API_BASE}/admin/event-formulas/${formulaId}`, {
        method: 'DELETE',
        headers: {
          ...authHeader(),
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Suppression impossible');
      }
      await fetchFormulas();
      if (editingFormulaId === formulaId) {
        resetForm();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      return;
    }

    const newFiles = [];
    const newPreviews = [];

    files.forEach((file) => {
      const isDuplicate = selectedPhotos.some(
        (existing) => existing.name === file.name && existing.size === file.size
      );
      if (!isDuplicate) {
        newFiles.push(file);
        newPreviews.push(URL.createObjectURL(file));
      }
    });

    if (newFiles.length === 0) {
      return;
    }

    setSelectedPhotos((prev) => [...prev, ...newFiles]);
    setPhotoPreviews((prev) => [...prev, ...newPreviews]);
  };

  const handleRemoveSelectedPhoto = (index) => {
    const preview = photoPreviews[index];
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
    setSelectedPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeletePhoto = async (formulaId, photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) {
      return;
    }
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/admin/event-formulas/${formulaId}/photos/${photoId}`,
        {
          method: 'DELETE',
          headers: {
            ...authHeader(),
          },
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Impossible de supprimer la photo');
      }
      const data = await res.json();
      const updated = data.formula;
      setFormulas((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item))
      );
      if (editingFormulaId === updated.id) {
        setEditingFormula(updated);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormValues((prev) => {
      const updated = [...prev.items];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return {
        ...prev,
        items: updated,
      };
    });
  };

  const handleAddItem = () => {
    setFormValues((prev) => ({
      ...prev,
      items: [...prev.items, createEmptyItem()],
    }));
  };

  const handleRemoveItem = (index) => {
    setFormValues((prev) => {
      if (prev.items.length === 1) {
        return prev;
      }
      const updated = prev.items.filter((_, idx) => idx !== index);
      return {
        ...prev,
        items: updated.length > 0 ? updated : [createEmptyItem()],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValues.name.trim()) {
      setError('Le nom de la formule est requis');
      return;
    }

    const payload = {
      name: formValues.name.trim(),
      description: formValues.description.trim() || null,
      is_visible: formValues.is_visible,
      items: (formValues.items || [])
        .map((item, index) => ({
          id: item.id,
          title: item.title.trim(),
          details: item.details.trim() || null,
          position: index,
        }))
        .filter((item) => item.title.length > 0),
    };

    setIsSaving(true);
    setError(null);
    try {
      const url = editingFormulaId
        ? `${API_BASE}/admin/event-formulas/${editingFormulaId}`
        : `${API_BASE}/admin/event-formulas`;
      const res = await fetch(url, {
        method: editingFormulaId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Enregistrement impossible");
      }
      let data = await res.json();
      let latestFormula = data.formula;
      let formulaId = editingFormulaId || latestFormula?.id;

      if (selectedPhotos.length > 0 && formulaId) {
        const formData = new FormData();
        selectedPhotos.forEach((photo) => formData.append('photos', photo));
        const photoRes = await fetch(
          `${API_BASE}/admin/event-formulas/${formulaId}/photos`,
          {
            method: 'POST',
            headers: {
              ...authHeader(),
            },
            body: formData,
          }
        );
        if (!photoRes.ok) {
          const err = await photoRes.json().catch(() => ({}));
          throw new Error(err.error || "Erreur lors du téléversement des photos");
        }
        data = await photoRes.json();
        latestFormula = data.formula;
      }

      await fetchFormulas();
      resetForm();
      if (latestFormula) {
        setFormulas((prev) => {
          const exists = prev.some((item) => item.id === latestFormula.id);
          if (exists) {
            return prev.map((item) =>
              item.id === latestFormula.id ? latestFormula : item
            );
          }
          return [latestFormula, ...prev];
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const formulaOptions = useMemo(
    () =>
      (formulas || []).map((formula) => ({
        value: formula.id,
        label: formula.name,
        description: formula.description,
        items: formula.items || [],
      })),
    [formulas]
  );

  const handleIncludeHiddenChange = (event) => {
    setIncludeHidden(event.target.checked);
  };

  return (
    <div className="event-formulas-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              <path d="M2 17l10 5 10-5"/>
              <path d="M2 12l10 5 10-5"/>
            </svg>
          </div>
          <div className="header-text">
            <h1>Formules Événement</h1>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{error}</span>
        </div>
      )}

      <div className="page-content">
        <section className="form-card">
          <div className="card-header">
            <h2>
              {editingFormulaId ? (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  Modifier la formule
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Créer une nouvelle formule
                </>
              )}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="formula-form">
            <div className="form-section">
              <h3 className="section-title">Informations générales</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">
                    Nom de la formule <span className="required">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    value={formValues.name}
                    onChange={handleInputChange}
                    placeholder="Ex. Formule Prestige"
                    disabled={isSaving}
                    required
                  />
                </div>
                {/* <div className="form-group switch-group">
                  <label htmlFor="is_visible" className="switch-container">
                    <input
                      type="checkbox"
                      id="is_visible"
                      name="is_visible"
                      checked={formValues.is_visible}
                      onChange={handleInputChange}
                      disabled={isSaving}
                    />
                    <span className="switch-slider"></span>
                    <span className="switch-label-text">Visible sur le site</span>
                  </label>
                </div> */}
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  value={formValues.description}
                  onChange={handleInputChange}
                  placeholder="Décrivez votre formule événement en quelques mots..."
                  disabled={isSaving}
                />
              </div>
            </div>

            <div className="form-section photos-section">
              <h3 className="section-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                Photos de la formule
              </h3>
              
              <div className="photos-upload">
                <label htmlFor="formulaPhotos" className="upload-button">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  Choisir des photos
                </label>
                <input
                  id="formulaPhotos"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoChange}
                  style={{ display: 'none' }}
                  disabled={isSaving}
                />
                <span className="help-text">JPG, PNG ou WebP • Max 5 Mo par photo</span>
              </div>

              {photoPreviews.length > 0 && (
                <div className="photos-container">
                  <h4 className="subsection-title">Nouvelles photos ({photoPreviews.length})</h4>
                  <div className="photos-grid">
                    {photoPreviews.map((src, index) => (
                      <div key={index} className="photo-item new">
                        <img src={src} alt={`Nouvelle ${index + 1}`} />
                        <button
                          type="button"
                          className="delete-photo-btn"
                          onClick={() => handleRemoveSelectedPhoto(index)}
                          disabled={isSaving}
                          title="Supprimer"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {editingFormula?.photos?.length > 0 && (
                <div className="photos-container">
                  <h4 className="subsection-title">Photos actuelles ({editingFormula.photos.length})</h4>
                  <div className="photos-grid">
                    {editingFormula.photos.map((photo) => (
                      <div key={photo.id} className="photo-item current">
                        <img src={getFullImageUrl(photo.url)} alt="" />
                        <button
                          type="button"
                          className="delete-photo-btn"
                          onClick={() => handleDeletePhoto(editingFormula.id, photo.id)}
                          disabled={isSaving}
                          title="Supprimer"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="form-section items-section">
              <div className="section-header">
                <h3 className="section-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="8" y1="6" x2="21" y2="6"/>
                    <line x1="8" y1="12" x2="21" y2="12"/>
                    <line x1="8" y1="18" x2="21" y2="18"/>
                    <line x1="3" y1="6" x2="3.01" y2="6"/>
                    <line x1="3" y1="12" x2="3.01" y2="12"/>
                    <line x1="3" y1="18" x2="3.01" y2="18"/>
                  </svg>
                  Prestations incluses
                </h3>
                <button
                  type="button"
                  className="add-item-button"
                  onClick={handleAddItem}
                  disabled={isSaving}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Ajouter une prestation
                </button>
              </div>

              <div className="items-list">
                {formValues.items.map((item, index) => (
                  <div className="item-row" key={index}>
                    <div className="item-number">{index + 1}</div>
                    <div className="item-fields">
                      <div className="form-group">
                        <label>Nom de la prestation</label>
                        <input
                          value={item.title}
                          onChange={(e) => handleItemChange(index, 'title', e.target.value)}
                          placeholder="Ex. DJ, Traiteur, Photographe..."
                          disabled={isSaving}
                        />
                      </div>
                      <div className="form-group">
                        <label>Détails</label>
                        <input
                          value={item.details}
                          onChange={(e) => handleItemChange(index, 'details', e.target.value)}
                          placeholder="Ex. 4h de prestation, menu 3 services..."
                          disabled={isSaving}
                        />
                      </div>
                    </div>
                    <button
  type="button"
  className="remove-item-button"
  onClick={() => handleRemoveItem(index)}
  disabled={isSaving || formValues.items.length === 1}
  title="Supprimer cette prestation"
>
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
</button>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-actions">
              {editingFormulaId && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={resetForm}
                  disabled={isSaving}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Annuler
                </button>
              )}
              <button type="submit" className="primary-button" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <span className="spinner"></span>
                    Enregistrement...
                  </>
                ) : editingFormulaId ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Mettre à jour
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                      <polyline points="17 21 17 13 7 13 7 21"/>
                      <polyline points="7 3 7 8 15 8"/>
                    </svg>
                    Créer la formule
                  </>
                )}
              </button>
            </div>
          </form>
        </section>

        <section className="list-card">
          <div className="card-header">
            <h2>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="8" y1="6" x2="21" y2="6"/>
                <line x1="8" y1="12" x2="21" y2="12"/>
                <line x1="8" y1="18" x2="21" y2="18"/>
                <line x1="3" y1="6" x2="3.01" y2="6"/>
                <line x1="3" y1="12" x2="3.01" y2="12"/>
                <line x1="3" y1="18" x2="3.01" y2="18"/>
              </svg>
              Mes formules ({formulas.length})
            </h2>
            <div className="list-controls">
              <div className="list-search">
                <Select
                  classNamePrefix="formula-select"
                  placeholder="🔍 Rechercher une formule..."
                  isClearable
                  options={formulaOptions}
                  onChange={(option) => {
                    if (option) {
                      const formula = formulas.find((f) => f.id === option.value);
                      if (formula) {
                        handleEdit(formula);
                      }
                    }
                  }}
                  />
                </div>
                {/* <div className="list-filters">
                  <label className="switch-container">
                    <input
                      type="checkbox"
                      checked={includeHidden}
                      onChange={handleIncludeHiddenChange}
                      disabled={loading}
                    />
                    <span className="switch-slider"></span>
                    <span className="switch-label-text">Afficher les formules masquées</span>
                  </label>
                </div> */}
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner large"></div>
              <p>Chargement des formules...</p>
            </div>
          ) : formulas.length === 0 ? (
            <div className="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
              <h3>Aucune formule créée</h3>
              <p>Créez votre première formule pour regrouper vos prestations événementielles</p>
            </div>
          ) : (
            <div className="formula-list">
              {formulas.map((formula) => (
                <div className="formula-item" key={formula.id}>
                  <div className="formula-item-header">
                    <div className="formula-title-section">
                      <h3>{formula.name}</h3>
                      <span className={`visibility-badge ${formula.is_visible ? 'visible' : 'hidden'}`}>
                        {formula.is_visible ? (
                          <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                            Visible
                          </>
                        ) : (
                          <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                              <line x1="1" y1="1" x2="23" y2="23"/>
                            </svg>
                            Masquée
                          </>
                        )}
                      </span>
                    </div>
                    {formula.description && (
                      <p className="formula-description">{formula.description}</p>
                    )}
                  </div>

                  {formula.photos && formula.photos.length > 0 && (
                    <div className="formula-photos">
                      <div className="photo-thumbnails">
                        {formula.photos.slice(0, 4).map((photo) => (
                          <div key={photo.id} className="photo-thumbnail-wrapper">
                            <img
                              src={getFullImageUrl(photo.url)}
                              alt=""
                              className="photo-thumbnail"
                            />
                          </div>
                        ))}
                        {formula.photos.length > 4 && (
                          <div className="more-photos-badge">
                            +{formula.photos.length - 4}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {formula.items && formula.items.length > 0 ? (
                    <div className="items-preview">
                      <h4 className="items-preview-title">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        {formula.items.length} prestation{formula.items.length > 1 ? 's' : ''} incluse{formula.items.length > 1 ? 's' : ''}
                      </h4>
                      <ul className="items-compact-list">
                        {formula.items.map((item) => (
                          <li key={item.id}>
                            <span className="item-title">{item.title}</span>
                            {item.details && (
                              <span className="item-details-text">— {item.details}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="no-items-message">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="8" x2="12" y2="12"/>
                          <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        Aucune prestation définie
                      </div>
                    )}
  
                    <div className="formula-actions">
                      <button
                        type="button"
                        className="action-button edit"
                        onClick={() => handleEdit(formula)}
                        disabled={isSaving}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                        Modifier
                      </button>
                      <button
                        type="button"
                        className="action-button delete"
                        onClick={() => handleDelete(formula.id)}
                        disabled={isSaving}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          <line x1="10" y1="11" x2="10" y2="17"/>
                          <line x1="14" y1="11" x2="14" y2="17"/>
                        </svg>
                        Supprimer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    );
  }
  
  export default EventFormulasPage;